import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { allEpisodes, paginate, totalPages } from "@/utils/content";
import { arcList } from "@/mocks/episodes";

const PAGE_SIZE = 6;

export default function Episodes() {
  const [arc, setArc] = useState("全部");
  const [sort, setSort] = useState<"order" | "latest">("order");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = allEpisodes.filter((e) => arc === "全部" || e.arc === arc);
    return [...list].sort((a, b) =>
      sort === "order" ? a.number - b.number : b.number - a.number,
    );
  }, [arc, sort]);

  const pageCount = totalPages(filtered.length, PAGE_SIZE);
  const current = Math.min(page, pageCount);
  const visible = paginate(filtered, current, PAGE_SIZE);

  const resetFilters = () => {
    setArc("全部");
    setSort("order");
    setPage(1);
  };

  const handleArc = (value: string) => {
    setArc(value);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        eyebrow="剧集资料"
        icon="ri-film-line"
        title="火影忍者剧集全集"
        description="按故事篇章浏览剧集列表，每一集都配有剧情简介与出场角色，帮助你快速定位并理解经典情节。"
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {["全部", ...arcList].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => handleArc(item)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  arc === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-sort-desc" />
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as "order" | "latest");
                setPage(1);
              }}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="order">按剧集顺序</option>
              <option value="latest">按最新添加</option>
            </select>
          </label>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <strong className="text-foreground-900">{filtered.length}</strong> 集剧集
          {arc !== "全部" && ` · 篇章「${arc}」`}
        </p>

        {visible.length === 0 ? (
          <div className="mt-5">
            <EmptyState
              title="无匹配剧集"
              description="当前筛选条件下没有找到剧集，试试切换其它篇章。"
              onReset={resetFilters}
            />
          </div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((episode) => (
                <EpisodeCard key={episode.id} episode={episode} />
              ))}
            </div>
            <Pagination page={current} total={pageCount} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}